import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TraceData, TraceDataRef, TraceRow } from '../../api/trace';
import { TraceDataRefs } from './TraceDataRefs';
import { TraceValue } from './TraceIo';

export interface ITraceRowsProps {
    /** The step these rows belong to, so two tables cannot share an open state. */
    owner: string;

    rows: TraceRow[];

    /** Fetches the body in a row, when it has one and somebody opens it. */
    read: (reference: TraceDataRef) => Promise<TraceData>;
}

/** A moment, written the way the rest of the panel writes one. */
const moment = (iso?: string | null): string => {
    if (!iso) {
        return '';
    }

    const parsed = Date.parse(iso);

    return Number.isNaN(parsed)
        ? ''
        : new Date(parsed).toLocaleString('nl-NL', {
              day: '2-digit',
              month: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit',
          });
};

/**
 * Whether the row is there, as the three answers it actually has.
 *
 * Not two. "Weg" and "niet opgezocht" look the same on a screen that only knows yes and no,
 * and they send a reader to opposite conclusions: a stored copy that is gone means the send
 * handler finished with it, while one nobody looked for means nothing at all.
 */
const PRESENCE = {
    yes: {
        label: 'staat er',
        icon: 'circle-check' as const,
        hint: 'Deze rij staat er nu, onder deze twee sleutels.',
    },
    no: {
        label: 'weg',
        icon: 'circle-xmark' as const,
        hint: 'Onder deze twee sleutels staat niets. Bij deze tabellen is dat vaak het goede nieuws: de stap die eruit leest, ruimt de rij op zodra hij klaar is.',
    },
    unknown: {
        label: 'niet opgezocht',
        icon: 'circle-question' as const,
        hint: 'Er is hier niet gekeken, dus dit zegt niet dat de rij er niet is. De sleutels staan er wel, dus je kunt hem met de hand opzoeken.',
    },
};

/**
 * The rows one step writes, as the rows they are.
 *
 * The step above this used to say "het vertaalde object staat er nog" and stop there, which
 * is the summary and not the thing. Table Storage has no query worth the name: to look at
 * that row by hand you need the table, the partition key and the row key, spelled exactly,
 * and none of the three were on the screen. So they are here, in the shape the table has
 * them, with the columns this object put in them next to them.
 *
 * A row that is gone is drawn as fully as one that is there. That is the whole use of the
 * pairing - two tables, and which of them still holds a row says which half of the chain an
 * object is stuck in - and an absence you cannot see is an absence you cannot reason about.
 */
export const TraceRows = (props: ITraceRowsProps) => {
    if (props.rows.length === 0) {
        return null;
    }

    return (
        <div className="trace-rows">
            {props.rows.map((row, index) => {
                const presence =
                    row.exists === true
                        ? PRESENCE.yes
                        : row.exists === false
                        ? PRESENCE.no
                        : PRESENCE.unknown;

                const state =
                    row.exists === true
                        ? 'present'
                        : row.exists === false
                        ? 'gone'
                        : 'unknown';

                return (
                    <div
                        className={`trace-rows__table trace-rows__table--${state}`}
                        key={`${row.table}-${row.partitionKey}-${row.rowKey}-${index}`}
                    >
                        <div className="trace-rows__head">
                            <FontAwesomeIcon
                                icon="table"
                                className="trace-rows__icon"
                            />

                            <code className="trace-rows__name">
                                {row.table ?? 'tabel onbekend'}
                            </code>

                            <span
                                className={`trace-rows__presence trace-rows__presence--${state}`}
                                title={presence.hint}
                            >
                                <FontAwesomeIcon icon={presence.icon} />
                                {presence.label}
                            </span>

                            {row.at && (
                                <span
                                    className="trace-rows__at"
                                    title="Wanneer deze rij voor het laatst is geschreven."
                                >
                                    {moment(row.at)}
                                </span>
                            )}
                        </div>

                        {row.hint && (
                            <p className="trace-rows__hint">{row.hint}</p>
                        )}

                        {/*
                            The two keys first and apart from the rest, because they are not
                            ordinary columns: they are the whole address of the row, and they
                            are what somebody copies into a storage browser. Which is also why
                            each of them carries its own copy button.
                        */}
                        <table className="trace-rows__grid">
                            <tbody>
                                <tr className="trace-rows__row trace-rows__row--key">
                                    <th scope="row" title="De partitiesleutel van deze rij.">
                                        PartitionKey
                                    </th>
                                    <td>
                                        {row.partitionKey ? (
                                            <TraceValue
                                                value={row.partitionKey}
                                                label="PartitionKey"
                                            />
                                        ) : (
                                            <em>onbekend</em>
                                        )}
                                    </td>
                                </tr>

                                <tr className="trace-rows__row trace-rows__row--key">
                                    <th scope="row" title="De rijsleutel van deze rij.">
                                        RowKey
                                    </th>
                                    <td>
                                        {row.rowKey ? (
                                            <TraceValue
                                                value={row.rowKey}
                                                label="RowKey"
                                            />
                                        ) : (
                                            <em>onbekend</em>
                                        )}
                                    </td>
                                </tr>

                                {row.columns.map((cell) => (
                                    <tr className="trace-rows__row" key={cell.name}>
                                        <th
                                            scope="row"
                                            title={cell.hint ?? undefined}
                                        >
                                            {cell.name}
                                        </th>
                                        <td
                                            className={
                                                cell.mono
                                                    ? 'trace-detail__mono'
                                                    : undefined
                                            }
                                        >
                                            {cell.value ?? (
                                                <em className="trace-rows__blank">
                                                    leeg
                                                </em>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {row.data && (
                            <TraceDataRefs
                                owner={`${props.owner}|row|${index}`}
                                refs={[row.data]}
                                read={props.read}
                            />
                        )}
                    </div>
                );
            })}
        </div>
    );
};
